import React, { useState, useEffect } from 'react';
import { History, Search, Filter, Package, Minus, Plus, RotateCcw, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getRewardMovements, getCompanyRewards } from '../../services/rewardsService';
import LoadingSpinner from '../common/LoadingSpinner';

const RewardsHistory = () => {
  const { user } = useAuth();
  const [movements, setMovements] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filtros
  const [filters, setFilters] = useState({
    rewardName: '',
    batchCode: '',
    type: 'all',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    if (user?.companyId) {
      loadData();
    }
  }, [user?.companyId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Carregar prêmios e movimentações em paralelo
      const [movementsData, rewardsData] = await Promise.all([
        getRewardMovements(user.companyId),
        getCompanyRewards(user.companyId, false) // Incluir inativos
      ]);
      
      setMovements(movementsData);
      setRewards(rewardsData);
    } catch (err) {
      setError('Erro ao carregar histórico: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (movementList) => {
    return movementList.filter(movement => {
      // Filtro por nome do prêmio
      if (filters.rewardName && !movement.rewardName?.toLowerCase().includes(filters.rewardName.toLowerCase())) {
        return false;
      }

      // Filtro por código de lote
      if (filters.batchCode && (!movement.batchCode || !movement.batchCode.toLowerCase().includes(filters.batchCode.toLowerCase()))) {
        return false;
      }

      // Filtro por tipo de movimentação
      if (filters.type !== 'all' && movement.type !== filters.type) {
        return false;
      }

      // Filtro por data
      if (filters.dateFrom) {
        const movementDate = movement.createdAt?.toDate?.() || new Date(movement.createdAt);
        const filterDate = new Date(filters.dateFrom);
        if (movementDate < filterDate) {
          return false;
        }
      }

      if (filters.dateTo) {
        const movementDate = movement.createdAt?.toDate?.() || new Date(movement.createdAt);
        const filterDate = new Date(filters.dateTo + 'T23:59:59');
        if (movementDate > filterDate) {
          return false;
        }
      }

      return true;
    });
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'stock_add':
        return <Plus size={16} className="text-green-600" />;
      case 'stock_remove':
        return <Minus size={16} className="text-red-600" />;
      case 'redemption':
        return <Package size={16} className="text-blue-600" />;
      case 'adjustment':
        return <RotateCcw size={16} className="text-yellow-600" />;
      default:
        return <History size={16} className="text-gray-600" />;
    }
  };

  const getMovementTypeLabel = (type) => {
    const labels = {
      stock_add: 'Adição de Estoque',
      stock_remove: 'Redução de Estoque',
      redemption: 'Resgate',
      adjustment: 'Ajuste de Estoque'
    };
    return labels[type] || type;
  };

  const getMovementTypeColor = (type) => {
    const colors = {
      stock_add: 'bg-green-100 text-green-800',
      stock_remove: 'bg-red-100 text-red-800',
      redemption: 'bg-blue-100 text-blue-800',
      adjustment: 'bg-yellow-100 text-yellow-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Data não disponível';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('pt-BR');
    } catch {
      return 'Data inválida';
    }
  };

  const clearFilters = () => {
    setFilters({
      rewardName: '',
      batchCode: '',
      type: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const exportData = () => {
    const filteredMovements = applyFilters(movements);
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Data,Prêmio,Tipo,Quantidade,Estoque Anterior,Estoque Novo,Lote,Motivo,Observações\n"
      + filteredMovements.map(movement => 
          `"${formatDateTime(movement.createdAt)}","${movement.rewardName}","${getMovementTypeLabel(movement.type)}","${movement.quantity}","${movement.previousStock}","${movement.newStock}","${movement.batchCode || '-'}","${movement.reason}","${movement.notes}"`
        ).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `historico-premios-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredMovements = applyFilters(movements);

  if (loading && movements.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <History className="mr-2" />
            Histórico de Movimentações
          </h1>
          <p className="text-gray-600 mt-1">
            Acompanhe todas as movimentações de estoque dos seus prêmios
          </p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={exportData}
            disabled={filteredMovements.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={16} />
            Exportar CSV
          </button>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RotateCcw size={16} />
            Atualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900 flex items-center">
            <Filter size={16} className="mr-2" />
            Filtros
          </h3>
          <button
            onClick={clearFilters}
            className="text-sm text-gray-600 hover:text-gray-800 underline"
          >
            Limpar filtros
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prêmio
            </label>
            <input
              type="text"
              value={filters.rewardName}
              onChange={(e) => setFilters(prev => ({ ...prev, rewardName: e.target.value }))}
              placeholder="Nome do prêmio..."
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código de Lote
            </label>
            <input
              type="text"
              value={filters.batchCode}
              onChange={(e) => setFilters(prev => ({ ...prev, batchCode: e.target.value }))}
              placeholder="Código do lote..."
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Movimentação
            </label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos os tipos</option>
              <option value="stock_add">Adição de Estoque</option>
              <option value="stock_remove">Redução de Estoque</option>
              <option value="redemption">Resgate</option>
              <option value="adjustment">Ajuste</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Inicial
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Final
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Total de Movimentações</div>
          <div className="text-2xl font-bold text-gray-900">{filteredMovements.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Resgates</div>
          <div className="text-2xl font-bold text-blue-600">
            {filteredMovements.filter(m => m.type === 'redemption').length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Adições de Estoque</div>
          <div className="text-2xl font-bold text-green-600">
            {filteredMovements.filter(m => m.type === 'stock_add').length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Reduções de Estoque</div>
          <div className="text-2xl font-bold text-red-600">
            {filteredMovements.filter(m => m.type === 'stock_remove').length}
          </div>
        </div>
      </div>

      {/* Lista de Movimentações */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data/Hora
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prêmio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantidade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estoque
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lote
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Motivo/Observações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMovements.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    <History size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium">Nenhuma movimentação encontrada</p>
                    <p className="text-sm">Tente ajustar os filtros ou adicionar produtos ao estoque</p>
                  </td>
                </tr>
              ) : (
                filteredMovements.map((movement) => (
                  <tr key={movement.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(movement.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {movement.rewardName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getMovementTypeColor(movement.type)}`}>
                        {getMovementIcon(movement.type)}
                        {getMovementTypeLabel(movement.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={movement.type === 'redemption' || movement.type === 'stock_remove' ? 'text-red-600' : 'text-green-600'}>
                        {movement.type === 'redemption' || movement.type === 'stock_remove' ? '-' : '+'}
                        {movement.quantity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="text-gray-500">{movement.previousStock}</span>
                      <span className="mx-1">→</span>
                      <span className="font-medium">{movement.newStock}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {movement.batchCode ? (
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-mono">
                          {movement.batchCode}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{movement.reason}</div>
                        {movement.notes && (
                          <div className="text-gray-500 text-xs mt-1">{movement.notes}</div>
                        )}
                        {movement.userName && (
                          <div className="text-gray-400 text-xs mt-1">
                            por: {movement.userName}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {loading && movements.length > 0 && (
        <div className="flex justify-center mt-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

export default RewardsHistory;